import { useMemo, useState } from 'react';
import { REPORT_PERMISSIONS } from '@/constants/permissions';
import { AdvancedFilterButton, FilterPanelCollapse } from '@/components/common/AdvancedFilter';
import { DataTable } from '@/components/common/DataTable';
import { DateRangeBadge } from '@/components/common/DateRangeBadge';
import { ErrorState } from '@/components/common/ErrorState';
import { LabeledPillList } from '@/components/common/LabeledPill';
import { StatCardGrid } from '@/components/common/StatCardGrid';
import { PageHeader } from '@/components/layout/PageHeader';
import { FIT_TO_SCREEN_PAGE_CLASS } from '@/components/layout/page.constants';
import { ZM_SEARCH_PLACEHOLDER } from '@/constants/app.constants';
import { useInfiniteTableQuery } from '@/hooks/useInfiniteTableQuery';
import { useRevealLastViewedRow } from '@/hooks/useLastViewedRow';
import { cn } from '@/lib/utils';
import { REFETCH_FADE_TRANSITION_CLASS, REFETCHING_OPACITY_CLASS } from '@/styles/refetchFade';
import { getTableEmptyText } from '@/utils/listSearch';
import {
  downloadSystemDashboard,
  systemDashboardInfiniteQueryOptions,
} from '../api/dashboards.api';
import { ExportButton } from '@/components/common/ExportButton';
import { ReportingPeriodFilterPanel } from '../components/ReportingPeriodFilterPanel';
import { getSystemDashboardColumns } from '../components/SystemDashboardColumns';
import { useReportingPeriodFilters } from '../hooks/useReportingPeriodFilters';
import { toSystemDrillSearch } from '../utils/reportingPeriodFilters';
import {
  getSystemDashboardStatCards,
  getSystemDashboardSummaryPills,
} from '../utils/systemDashboardSummary';

export function SystemDashboardPage() {
  const controller = useReportingPeriodFilters('system');
  const { rows, firstPageMeta, isLoading, isRefetching, loadError, refetch, loadMoreProps } =
    useInfiniteTableQuery(systemDashboardInfiniteQueryOptions(controller.params));

  const [isFilterOpen, setIsFilterOpen] = useState(false);
  const lastViewedUserId = useRevealLastViewedRow('systemDashboardMembers');

  const columns = useMemo(
    () =>
      getSystemDashboardColumns(toSystemDrillSearch(controller.params), controller.params.search),
    [controller.params],
  );

  const totals = firstPageMeta?.totals;
  const zmCount = firstPageMeta?.total;
  const statCards = useMemo(
    () => getSystemDashboardStatCards({ totals, zmCount }),
    [totals, zmCount],
  );
  const summaryPills = useMemo(
    () => getSystemDashboardSummaryPills({ totals, zmCount }),
    [totals, zmCount],
  );

  return (
    <div className={FIT_TO_SCREEN_PAGE_CLASS}>
      <PageHeader
        title="System Dashboard"
        titleAddon={
          <DateRangeBadge
            onClick={() => setIsFilterOpen(true)}
            dateFrom={controller.params.dateFrom}
            dateTo={controller.params.dateTo}
          />
        }
        search={{
          value: controller.searchText,
          onChange: controller.setSearchText,
          placeholder: ZM_SEARCH_PLACEHOLDER,
        }}
        actions={
          <>
            <AdvancedFilterButton
              isOpen={isFilterOpen}
              activeCount={controller.activeFilterCount}
              onClick={() => setIsFilterOpen((wasOpen) => !wasOpen)}
            />
            <ExportButton
              requires={REPORT_PERMISSIONS.SYSTEM_DASHBOARD_EXPORT}
              onExport={() => downloadSystemDashboard(controller.params)}
              disabled={isLoading || loadError !== null}
            />
          </>
        }
      />

      <FilterPanelCollapse isOpen={isFilterOpen}>
        <ReportingPeriodFilterPanel controller={controller} />
      </FilterPanelCollapse>

      {loadError ? (
        <ErrorState
          error={loadError}
          fallbackTitle="Could not load the dashboard"
          onRetry={() => void refetch()}
          fill
        />
      ) : (
        <>
          <StatCardGrid isLoading={isLoading} isFetching={isRefetching} stats={statCards} />

          {summaryPills.length > 0 ? (
            <div
              className={cn(
                REFETCH_FADE_TRANSITION_CLASS,
                isRefetching && REFETCHING_OPACITY_CLASS,
              )}
            >
              <LabeledPillList fields={summaryPills} />
            </div>
          ) : null}

          <DataTable
            data={rows}
            columns={columns}
            isLoading={isLoading}
            className="lg:flex-1"
            stickyColumnId="name"
            {...getTableEmptyText(
              controller.params.search,
              'No activity for these filters',
              'Try a wider date range.',
            )}
            isRowHighlighted={(member) => member.userId === lastViewedUserId}
            {...loadMoreProps}
          />
        </>
      )}
    </div>
  );
}
